import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query and returns the current match boolean.
 *
 * SSR-safe: starts as `false` on the first render so the server-rendered
 * markup matches the client-rendered markup; the first effect tick then
 * syncs to the real window state. This avoids a flash of mobile layout on
 * desktop and vice versa.
 *
 * Uses `addEventListener("change", …)` — `addListener` is the deprecated
 * Safari < 14 fallback we do not need to support.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * `< md` (< 768px) — phones, including the long edge of small foldables.
 *
 * Treat as "single column, bottom-nav, full-screen sheets" layout.
 */
export function useIsMobile(): boolean {
  return useMediaQuery("(max-width: 767.98px)");
}

